import { EnhancedScopeDataWithBranchName } from '../../scopeData/enhanceScopeData';
import { Graphics } from '../../scopeData/types';
import { withComponentHierarchyPopover } from '../../DiffMergeTablePopover/utils';
import { APIEntityType, ArdoqId } from '@ardoq/api-types';
import { Fragment, useRef } from 'react';
import {
  ENTITY_ID_ATTRIBUTE_KEY,
  EntityName,
  FlexWrapper,
  IconContainer,
} from './atoms';
import { ComponentIconRepresentation } from './ComponentIconRepresentation';
import { Link } from '@ardoq/link';
import { EnhancedScopeData } from '@ardoq/data-model';
import { isEnhancedScopeDataWithBranchName } from './utils';
import { useIsTextTruncated } from '@ardoq/hooks';
import { readRawValue } from '../../scopeData/utils/readRawValue';

export type ComponentLinkHandler = {
  href: string;
  onClick: VoidFunction;
  tooltipText?: string;
};

const ComponentRepresentationWithLink = ({
  entityId,
  enhancedScopeData,
  graphics,
  isCommaSeparated,
  hasLabelEllipsis,
  hideDefaultPopover,
  shouldHaveWrapper,
  componentLinkHandler,
  underlineOnHover,
  linkColor,
  variant,
}: {
  entityId: ArdoqId;
  enhancedScopeData: EnhancedScopeDataWithBranchName | EnhancedScopeData;
  graphics: Graphics;
  isCommaSeparated?: boolean;
  hasLabelEllipsis?: boolean;
  hideDefaultPopover?: boolean;
  shouldHaveWrapper?: boolean;
  componentLinkHandler?: ComponentLinkHandler;
  underlineOnHover?: boolean;
  linkColor?: string;
  variant?: 'default' | 'light';
}) => {
  const WrapperComponent = shouldHaveWrapper ? FlexWrapper : Fragment;
  const { onClick, tooltipText, href } = componentLinkHandler ?? {};

  const managesOverflow = variant === 'light' && Boolean(tooltipText);
  const nameRef = useRef<HTMLSpanElement>(null);
  const isNameTruncated = useIsTextTruncated(nameRef);
  const name = String(
    readRawValue(APIEntityType.COMPONENT, entityId, 'name', enhancedScopeData)
  );
  const combinedTooltipText = isNameTruncated
    ? [name, tooltipText].join('\n')
    : tooltipText;

  const style = {
    ...(hasLabelEllipsis
      ? { overflow: 'hidden', textOverflow: 'ellipsis' }
      : {}),
    ...(variant === 'light' ? { height: '24px' } : {}),
  };

  const RenderedEntityName = (
    <EntityName
      popoverAttributes={
        isEnhancedScopeDataWithBranchName(enhancedScopeData)
          ? withComponentHierarchyPopover({
              branch: enhancedScopeData.branchName,
              componentId: entityId,
            })
          : undefined
      }
      entityType={APIEntityType.COMPONENT}
      entityId={entityId}
      enhancedScopeData={enhancedScopeData}
      isCommaSeparated={isCommaSeparated}
      hasLabelEllipsis={hasLabelEllipsis}
      hideDefaultPopover={hideDefaultPopover}
      variant={variant}
      nameRef={managesOverflow ? nameRef : undefined}
    />
  );

  return (
    <WrapperComponent>
      <IconContainer>
        <ComponentIconRepresentation
          entityId={entityId}
          entityType={APIEntityType.COMPONENT}
          enhancedScopeData={enhancedScopeData}
          graphics={graphics}
        />
      </IconContainer>
      {componentLinkHandler ? (
        <Link
          color={linkColor}
          hideIcon
          type="primary"
          typography="text1"
          data-tooltip-text={combinedTooltipText}
          style={style}
          {...{ [ENTITY_ID_ATTRIBUTE_KEY]: entityId }}
          href={href}
          onClick={onClick}
          underlineOnHover={underlineOnHover}
        >
          {RenderedEntityName}
        </Link>
      ) : (
        RenderedEntityName
      )}
    </WrapperComponent>
  );
};

export { ComponentRepresentationWithLink };
